import { action, computed, makeObservable, observable, reaction } from "mobx";
import _ from "lodash";
import { BaseStore } from "stores/BaseStore";
import type {
    ISignalsBaseObject,
    SignalDataEndpoint,
    ISignalsSelectionTypes,
    DefinitionDataSelectionType,
    OrderRule,
    SignalsPersistedObjectFilters,
    SignalsParsedVizOptions,
} from "../SignalsModels";
import {
    DefinitionDataSelectionTypeMapping,
    SignalsObjectType,
} from "../SignalsModels";
import type { SignalsRequest } from "../SignalsModels";
import { GROUP_BY_OPTION_CONFIG } from "./ObjectConfiguration/GroupBy/groupByOptionConfig";
import type { IRootStore } from "stores/RootStore";
import {
    ApplicationFiltersStore,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import type { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFiltersInstanceStore } from "stores/ApplicationFilters/ApplicationFiltersInstanceStore";
import { ApplicationFilterItemsStore } from "stores/ApplicationFilters/ApplicationFilterItemsStore";
import { ApplicationFilterValuesStore } from "stores/ApplicationFilters/ApplicationFilterValuesStore";
import { SignalsService } from "services/SignalsService";
import { AuthStore } from "stores/AuthStore";
import { initWordsPhrasesItems } from "components/Conversations/Views/Drawer/components/DeepFilter/WordsPhrasesStore";
import type { EndpointBehaviorConfig } from "./_DataEndpointConfig/Types";
import {
    emptyEndpointConfig,
    getEndpointBehaviorConfig,
} from "./_DataEndpointConfig";
import DrilldownStore from "./Drilldown/DrilldownStore";
import { DrilldownSource } from "./Drilldown/DrilldownTypes";
import SignalsEndpointCacheStore from "../Store/SignalsEndpointCacheStore";
import { canAccessDevelopmentOnlyFeatures } from "utils/EnvironmentUtils";
import {
    compareFiltersByKey,
    getObjectFilterKeysFromFilters,
    getModeCompanionKeyForFilterKey,
    getPersistedObjectFilterConflictsAgainstSource,
    resolvePersistedObjectFiltersAgainstSource,
    synchronizePersistedObjectFiltersExtendedMetadata,
    isExtendedMetadataObjectFilterKey,
    type FiltersComparisonResult,
    isFilterValueEmpty,
} from "./ObjectFilters";

// The value for the error property when a refresh operation is aborted
export const REFRESH_ABORT_ERROR_MESSAGE = "Refresh operation was aborted.";

function normalizeWordsAndPhrasesSearchString(
    value?: string,
): string | undefined {
    const normalizedValue = value?.trim();
    return normalizedValue && normalizedValue.length > 0
        ? normalizedValue
        : undefined;
}

function combineWordsAndPhrasesSearchStrings(
    sourceValue?: string,
    objectValue?: string,
): string | undefined {
    const normalizedSourceValue =
        normalizeWordsAndPhrasesSearchString(sourceValue);
    const normalizedObjectValue =
        normalizeWordsAndPhrasesSearchString(objectValue);

    if (!normalizedSourceValue) return normalizedObjectValue;
    if (!normalizedObjectValue) return normalizedSourceValue;
    if (normalizedSourceValue === normalizedObjectValue) {
        return normalizedSourceValue;
    }

    return `(${normalizedSourceValue}) & (${normalizedObjectValue})`;
}

export interface BaseObjectStoreParams<
    TData,
    TBaseObject = ISignalsBaseObject,
> {
    baseObject: TBaseObject;
    rootStore: IRootStore;
    options?: BaseObjectOptions;
    initialData?: TData;
    refreshOnCreate?: boolean;
}

export interface BaseObjectOptions {
    static?: boolean; // If true, the component does not refresh data after initial load
    isEditable?: boolean; // If true, the component can be edited
    isDeletable?: boolean; // If true, the component can be deleted
    isPresentationMode?: boolean; // If true, the component is in presentation mode
    isSaveable?: boolean; // If true, the component can save changes (e.g., persist selection updates)
    isDashboardSelected?: () => boolean; // Callback indicating whether this component's parent dashboard is currently selected
    getDashboardAppliedFilters?: () => ApplicationFilters | null; // Provides the dashboard's committed/applied filters for object-filter source clamping
}

export type RefreshOrigin = "standalone" | "dashboard-batch";

export type BaseRefreshOptions = {
    origin?: RefreshOrigin;
};

export type RefreshResult =
    | {
          status: "completed";
      }
    | {
          status: "deferred";
          waitFor: Promise<void>;
      };

type BaseObjectCacheEntry<TData> = {
    data: TData;
    payload?: unknown;
};

/**
 * BaseObjectStore reflects the shared properties of all Signals component objects (chart, text card, montage, small card).
 * Extend this for each specific component type store. Add the component-specific properties and methods in the derived class.
 * @template TData The type of data that the component will manage and display. This is the type returned by getData.
 * @template TState The type of state specific to the derived component store.
 * @template TCachePayload The type of data stored in the cache
 */
abstract class BaseObjectStore<
    TData,
    TState,
    TCachePayload = unknown,
> extends BaseStore {
    private static readonly CACHE_HIT_REFRESH_DELAY_MS = 250;

    // Properties common to all Signals component objects
    @observable id: string;
    @observable title?: string;
    @observable subtitle?: string;
    @observable dataEndpoint?: SignalDataEndpoint;
    @observable data?: TData;
    @observable state?: TState;
    @observable order: number;
    @observable objectType: SignalsObjectType;
    @observable orderRuleId?: string | null;
    @observable.ref orderRule?: OrderRule | null;
    @observable vizOptions: string | null = null;
    @observable parsedVizOptions: SignalsParsedVizOptions | null = null;
    @observable isEditable: boolean; // Tracks whether we can edit this component. Think being able to edit viz options or name etc
    @observable isDeletable: boolean; // Tracks whether we can delete this component.
    @observable isPresentationMode: boolean; // Tracks whether we are in presentation mode
    @observable isSaveable: boolean; // Tracks whether we can save changes to this component (e.g., persist selection updates)

    /**
     * Shared "view-only" concept for a card. A read-only card still renders its
     * controls so viewers can see what is selected, but those controls disable
     * themselves off this flag rather than each re-deriving `!isEditable`. AUT-8882.
     */
    @computed get isReadOnly(): boolean {
        return !this.isEditable;
    }

    // error tracks refresh failures, generally from the api/getData function; abortError is a simple flag that a cancellation occurred
    @observable error: string | null = null;
    @observable abortError: boolean | null = null;

    // Used to manage aborting of refresh requests
    protected abortController: AbortController | null = null;
    // Snapshots used to restore pre-refresh state when a batch is cancelled
    private previousData?: TData;
    private previousError: string | null = null;
    private previousAbortError: boolean | null = null;
    // Per-object source-filter baseline from this object's last successful refresh.
    // Different from DashboardStore.lastAppliedFilters: this tracks what this specific object actually refreshed with,
    // so object-filter restrictions stay aligned with rendered data and can roll back correctly on cancel.
    @observable.ref private lastAppliedSourceFilters: ApplicationFilters | null =
        null;
    private previousLastAppliedSourceFilters: ApplicationFilters | null = null;
    @observable.ref private lastAppliedObjectFiltersPayload: SignalsPersistedObjectFilters | null =
        null;
    private previousLastAppliedObjectFiltersPayload: SignalsPersistedObjectFilters | null =
        null;
    private bypassCacheOnce: boolean = false;
    static: boolean = false; // Set this in a child store constructor to tell the refresher to not do anything for that type of component

    protected readonly authStore: AuthStore;
    protected readonly signalsService: SignalsService;
    protected readonly applicationFiltersStore: ApplicationFiltersStore;
    protected readonly applicationFilterItemsStore: ApplicationFilterItemsStore;
    protected readonly applicationFilterValuesStore: ApplicationFilterValuesStore;
    protected readonly signalsEndpointCacheStore: SignalsEndpointCacheStore;
    protected readonly rootStore: IRootStore;
    protected readonly isDashboardSelected?: () => boolean;
    protected readonly getDashboardAppliedFilters?: () => ApplicationFilters | null;
    private dashboardAppliedFiltersStore: ApplicationFiltersInstanceStore | null =
        null;

    /** Behavior and capability configuration for this chart instance. */
    @observable config: EndpointBehaviorConfig;

    /** Drilldown store enabling deeper exploration for supported charts. */
    @observable drilldownStore: DrilldownStore | null = null;

    /**
     * Draft object-level filter store. This is mutable via configuration UIs and only
     * affects requests once committed.
     */
    @observable objectFiltersStore: ApplicationFiltersInstanceStore | null =
        null;
    /** Draft toggle controlling whether object-level dates override dashboard dates. */
    @observable objectFilterDateOverride: boolean = false;
    /** Committed date-override toggle backing request generation. */
    @observable protected _committedDateOverride: boolean = false;
    /** Draft toggle controlling whether object-level date type is explicitly set. */
    @observable objectFilterDateTypeOverride: boolean = false;
    /** Committed date-type override toggle backing request generation. */
    @observable protected _committedDateTypeOverride: boolean = false;
    /** Draft toggle controlling whether call duration is explicitly set at object level. */
    @observable objectFilterCallDurationOverride: boolean = false;
    /** Committed call-duration override toggle backing chip/persistence behavior. */
    @observable protected _committedCallDurationOverride: boolean = false;
    /** Draft toggle controlling whether message count is explicitly set at object level. */
    @observable objectFilterMessageCountOverride: boolean = false;
    /** Committed message-count override toggle backing chip/persistence behavior. */
    @observable protected _committedMessageCountOverride: boolean = false;

    /** Whether this store currently supports point-selection drilldowns. */
    @computed get isPointMenuEnabled() {
        return (
            this.config.capabilities.pointSelectMenu && !!this.drilldownStore
        );
    }

    constructor({
        baseObject,
        rootStore,
        options,
        initialData,
        refreshOnCreate,
    }: BaseObjectStoreParams<TData>) {
        super("Base Component Store");
        makeObservable(this);
        this.static = options?.static ?? false;
        this.isEditable = options?.isEditable ?? false;
        this.isDeletable = options?.isDeletable ?? false;
        this.isPresentationMode = options?.isPresentationMode ?? false;
        this.isSaveable = options?.isSaveable ?? false;
        this.isDashboardSelected = options?.isDashboardSelected;
        this.getDashboardAppliedFilters = options?.getDashboardAppliedFilters;
        this.id = baseObject.id;
        this.title = baseObject.title;
        this.subtitle = baseObject.subtitle;
        this.dataEndpoint = baseObject.dataEndpoint;
        this.order = baseObject.order;
        this.objectType = baseObject.objectType;
        this.orderRuleId = baseObject.orderRuleId;
        this.orderRule = baseObject.orderRule;

        this.abortController = new AbortController();

        this.authStore = rootStore.getStore(AuthStore);
        this.rootStore = rootStore;
        this.signalsService = new SignalsService();
        this.applicationFiltersStore = rootStore.getStore(
            ApplicationFiltersStore,
        );
        this.applicationFilterItemsStore = rootStore.getStore(
            ApplicationFilterItemsStore,
        );
        this.applicationFilterValuesStore = rootStore.getStore(
            ApplicationFilterValuesStore,
        );
        this.signalsEndpointCacheStore = rootStore.getStore(
            SignalsEndpointCacheStore,
        );
        this.initializeVizOptions(baseObject.vizOptions);

        // If we have a data endpoint, get the behavior config for this component.
        // Try to derive selectionType from the base object (charts provide typeOfDataSelectedInChart),
        // fall back to undefined when not available so config uses default behavior.
        const selectionTypeFromBaseObject: ISignalsSelectionTypes | undefined =
            (baseObject as any)?.typeOfDataSelectedInChart !== undefined
                ? DefinitionDataSelectionTypeMapping[
                      (baseObject as any)
                          .typeOfDataSelectedInChart as DefinitionDataSelectionType
                  ]
                : undefined;

        if (this.dataEndpoint) {
            this.config = getEndpointBehaviorConfig({
                dataEndpoint: this.dataEndpoint,
                selectionType: selectionTypeFromBaseObject,
            });
        } else {
            this.config = emptyEndpointConfig;
        }

        if (this.config.capabilities.objectFilters) {
            this.objectFiltersStore = new ApplicationFiltersInstanceStore(
                this.rootStore,
                `object-filters-${this.id}`,
            );
            this.hydratePersistedObjectFilters(baseObject.objectFilters);
            reaction(
                () =>
                    this.applicationFilterItemsStore.extendedMetadataFields.map(
                        (field) => `${field.name}:${field.isGroupable}`,
                    ),
                () => {
                    void this.synchronizeObjectFilterExtendedMetadata();
                },
                { fireImmediately: true },
            );
        }

        // If data was provided in the base object, use it as our initial data
        if (initialData) {
            this.data = initialData;
        }

        // Establish an initial snapshot so the first cancel has something to restore to
        this.previousData = this.data;
        this.previousError = this.error;
        this.previousAbortError = this.abortError;
        this.previousLastAppliedSourceFilters = _.cloneDeep(
            this.lastAppliedSourceFilters,
        );

        if (refreshOnCreate) {
            // Immediately kick off a data refresh when created
            this.runRefresh();
        }
    }

    /** Cache Helpers **/
    protected getCacheFiltersPart(): unknown {
        return this.getMergedFiltersForRequest();
    }

    protected getCacheSelectionsPart(): unknown {
        return undefined;
    }

    protected getCacheExtraParts(): unknown {
        return undefined;
    }

    protected getCachePayload(): TCachePayload | undefined {
        return undefined;
    }

    protected restoreFromCachePayload(_payload: TCachePayload): void {
        return;
    }

    /**
     * Org-level data this object's request or cache key is derived from, and which therefore has
     * to be loaded before either is built. Overrides should delegate to loaders that return
     * immediately once loaded, so only the first refresh of a session actually waits.
     */
    protected loadRefreshPrerequisites(): Promise<void> {
        return Promise.resolve();
    }

    protected isCachingEnabled(): boolean {
        return !this.static;
    }

    private buildCacheKey(): string {
        const cacheStrategy = this.config.renderOptions.cacheKeyStrategy;
        const includeFilters = cacheStrategy !== "selectionsChange";
        const includeSelections = cacheStrategy !== "filtersChange";

        const cacheKeyParts = {
            authScope: {
                effectiveOrgId: this.authStore.effectiveOrgId,
                effectiveUserId: this.authStore.effectiveUserId,
                selectedOrgId: this.authStore.orgStore?.selectedOrganization?.id,
            },
            endpoint: this.dataEndpoint ?? "__endpoint_undefined__",
            filters: includeFilters
                ? this.getCacheFiltersPart()
                : "__filters_ignored__",
            selections: includeSelections
                ? this.getCacheSelectionsPart()
                : "__selections_ignored__",
            extras: this.getCacheExtraParts(),
        };

        return this.stringifyNormalizedCacheValue(cacheKeyParts);
    }

    protected stringifyNormalizedCacheValue(value: unknown): string {
        return JSON.stringify(this.normalizeCacheValue(value));
    }

    protected normalizeCacheValue(
        value: unknown,
        seen: WeakSet<object> = new WeakSet(),
    ): unknown {
        if (value === undefined) return "__undefined__";
        if (value === null || typeof value !== "object") return value;
        if (value instanceof Date) return value.toISOString();

        if (value instanceof Set) {
            return Array.from(value)
                .map((entry) => this.normalizeCacheValue(entry, seen))
                .sort((left, right) =>
                    JSON.stringify(left).localeCompare(JSON.stringify(right)),
                );
        }

        if (value instanceof Map) {
            return Array.from(value.entries())
                .map(([entryKey, entryValue]) => ({
                    key: this.normalizeCacheValue(entryKey, seen),
                    value: this.normalizeCacheValue(entryValue, seen),
                }))
                .sort((left, right) =>
                    JSON.stringify(left.key).localeCompare(
                        JSON.stringify(right.key),
                    ),
                );
        }

        if (seen.has(value)) return "__circular_reference__";
        seen.add(value);

        if (Array.isArray(value)) {
            return value
                .map((entry) => this.normalizeCacheValue(entry, seen))
                .sort((left, right) =>
                    JSON.stringify(left).localeCompare(JSON.stringify(right)),
                );
        }

        const asRecord = value as Record<string, unknown>;
        return Object.keys(asRecord)
            .sort((left, right) => left.localeCompare(right))
            .reduce<Record<string, unknown>>((normalized, key) => {
                normalized[key] = this.normalizeCacheValue(asRecord[key], seen);
                return normalized;
            }, {});
    }

    /** Refresh Lifecycle **/
    private createRefreshAbortError(): Error {
        const error = new Error(REFRESH_ABORT_ERROR_MESSAGE);
        error.name = "AbortError";
        return error;
    }

    private async waitForCacheHitRefreshDelay(
        abortSignal: AbortSignal,
    ): Promise<void> {
        if (abortSignal.aborted) {
            throw this.createRefreshAbortError();
        }

        await new Promise<void>((resolve, reject) => {
            const timeout = setTimeout(() => {
                abortSignal.removeEventListener("abort", onAbort);
                resolve();
            }, BaseObjectStore.CACHE_HIT_REFRESH_DELAY_MS);

            const onAbort = () => {
                clearTimeout(timeout);
                abortSignal.removeEventListener("abort", onAbort);
                reject(this.createRefreshAbortError());
            };

            abortSignal.addEventListener("abort", onAbort, { once: true });
        });
    }

    // Refresh the component's data by calling getData, handling aborts and errors
    @action
    async runRefresh(
        forceRefresh: boolean = false,
        _options?: BaseRefreshOptions,
    ): Promise<RefreshResult> {
        void _options;
        if (this.static && this.data !== undefined) {
            // If we're a static component and already hydrated, skip refresh and clear loading flags
            this.clearTaskLoading("refresh");
            this.setError(null);
            this.setAbortError(false);
            return { status: "completed" };
        }

        this.preRefresh?.();

        if (forceRefresh) {
            this.bypassCacheOnce = true;
        }
        this.setError(null);
        this.setAbortError(null);
        // Abort if already aborted before starting
        if (this.abortController?.signal.aborted) {
            this.setAbortError(true);
            this.bypassCacheOnce = false;
            return { status: "completed" };
        }
        await this.setupAsyncTask("refresh", async () => {
            // stripInaccessibleFilters cannot drop a Question Result row until the
            // module catalog says which ones this viewer may use, and it fails open
            // until then. Wait here so a request is never issued — nor a cache key
            // built from one — carrying a filter the viewer is not allowed to apply.
            // The loader returns immediately once loaded, so only the first refresh
            // of a session actually waits.
            await this.applicationFilterValuesStore.loadQuestionResultFilterableModules();
            await this.loadRefreshPrerequisites();

            // Keep the last good data so an abort can restore it
            const originalData = this.data;
            const shouldUseCache =
                this.isCachingEnabled() && !!this.dataEndpoint;
            const cacheKey = shouldUseCache ? this.buildCacheKey() : undefined;
            try {
                // Listen for abort signal
                const abortSignal = this.abortController?.signal;
                if (!abortSignal) {
                    throw new Error("AbortController is not initialized.");
                }
                let cachedData: BaseObjectCacheEntry<TData> | undefined;

                if (shouldUseCache && cacheKey && this.dataEndpoint) {
                    if (!this.bypassCacheOnce) {
                        cachedData = this.signalsEndpointCacheStore.get<
                            BaseObjectCacheEntry<TData>
                        >({
                            endpoint: this.dataEndpoint,
                            key: cacheKey,
                            scope: "primaryData",
                        });
                    }
                }

                if (cachedData) {
                    await this.waitForCacheHitRefreshDelay(abortSignal);
                    this.data = cachedData.data;
                    this.restoreFromCachePayload(
                        cachedData.payload as TCachePayload,
                    );
                } else {
                    const nextData = await this.getData({ abortSignal });
                    this.data = nextData;
                    if (shouldUseCache && cacheKey && this.dataEndpoint) {
                        this.signalsEndpointCacheStore.set({
                            endpoint: this.dataEndpoint,
                            key: cacheKey,
                            value: {
                                data: nextData,
                                payload: this.getCachePayload(),
                            },
                            scope: "primaryData",
                        });
                    }
                }
                this.setAbortError(false);

                if (this.objectFiltersStore) {
                    const sourceFilters = this.getDashboardAppliedFilters?.();
                    if (sourceFilters) {
                        this.setLastAppliedSourceFilters(sourceFilters);
                    }
                    this.setLastAppliedObjectFiltersFromCommittedState();
                }
            } catch (error) {
                if ((error as any).name === "AbortError") {
                    // Abort: revert to prior data and mark a cancel
                    this.setAbortError(true);
                    this.data = originalData; // retain original data on abort
                } else {
                    this.setError("Error fetching data during refresh.");
                    this.setAbortError(false);
                    if (
                        canAccessDevelopmentOnlyFeatures({
                            requireUltraUser: true,
                        })
                    ) {
                        // The card already displays a user-facing error state.
                        // Keep technical diagnostics out of notification toasts.
                        console.error("Signals data refresh failed", error);
                    }
                }
            } finally {
                this.bypassCacheOnce = false;
            }
            this.postRefresh?.();
        });
        return { status: "completed" };
    }

    // Hook for derived stores to run logic whenever a refresh cycle starts
    public preRefresh?(): void;

    // Hook for derived stores to run logic whenever a refresh cycle ends
    public postRefresh(): void {
        if (this.config.capabilities.pointSelectMenu && !this.drilldownStore) {
            if (!this.dataEndpoint || !this.state) {
                return;
            }
            const drilldownSource = this as unknown as DrilldownSource;
            this.drilldownStore = new DrilldownStore(
                this.rootStore,
                drilldownSource,
            );
        }
    }

    /**
     * Presentation mode is toggled on the dashboard after its components already exist, so the
     * dashboard pushes the new value down rather than components reading a value frozen at
     * construction.
     */
    @action
    setIsPresentationMode = (val: boolean) => {
        this.isPresentationMode = val;
    };

    // Mark this component as queued to refresh so loading state shows immediately
    @action
    markRefreshQueued = () => {
        this.setError(null);
        this.setAbortError(null);
        this.setTaskLoading("refresh", true);
    };

    // Clear queued refresh state without running the refresh (e.g., when canceled)
    @action
    markRefreshCancelled = () => {
        // Roll back to the pre-refresh snapshot and label the state as aborted
        this.restoreRefreshSnapshot(true);
        this.clearTaskLoading("refresh");
    };

    // Abstract method to be implemented by subclasses to fetch data
    public abstract getData({
        abortSignal,
    }: {
        abortSignal: AbortSignal;
    }): Promise<TData>;

    @action
    cancelRefresh = () => {
        if (this.abortController) {
            this.abortController.abort();
        }
        this.abortController = new AbortController();
    };

    @action
    setError = (errorMessage: string | null) => {
        this.error = errorMessage;
    };

    @action
    setAbortError = (value: boolean | null) => {
        this.abortError = value;
    };

    @action
    captureRefreshSnapshot = () => {
        // Save pre-refresh state so we can fully restore if the batch is canceled
        this.previousData = this.data;
        this.previousError = this.error;
        this.previousAbortError = this.abortError;
        this.previousLastAppliedSourceFilters = _.cloneDeep(
            this.lastAppliedSourceFilters,
        );
        this.previousLastAppliedObjectFiltersPayload = _.cloneDeep(
            this.lastAppliedObjectFiltersPayload,
        );
    };

    @action
    restoreRefreshSnapshot = (markAbort: boolean) => {
        // Restore prior state; optionally mark that this restore was triggered by an abort
        this.data = this.previousData;
        this.error = this.previousError;
        this.abortError = markAbort ? true : this.previousAbortError;
        this.lastAppliedSourceFilters = _.cloneDeep(
            this.previousLastAppliedSourceFilters,
        );
        this.lastAppliedObjectFiltersPayload = _.cloneDeep(
            this.previousLastAppliedObjectFiltersPayload,
        );
    };

    @action
    commitRefreshSnapshot = () => {
        // Successful refresh: make the new state the rollback baseline
        this.previousData = this.data;
        this.previousError = this.error;
        this.previousAbortError = this.abortError;
        this.previousLastAppliedSourceFilters = _.cloneDeep(
            this.lastAppliedSourceFilters,
        );
        this.previousLastAppliedObjectFiltersPayload = _.cloneDeep(
            this.lastAppliedObjectFiltersPayload,
        );
    };

    @action
    private setLastAppliedSourceFilters(sourceFilters: ApplicationFilters) {
        this.lastAppliedSourceFilters = _.cloneDeep(sourceFilters);
    }

    @computed get isRefreshing(): boolean {
        return this.getTaskLoading("refresh");
    }

    @computed get isSummaryLoading(): boolean {
        return false;
    }

    protected canAutoRefreshInCurrentContext(): boolean {
        return this.isDashboardSelected ? this.isDashboardSelected() : true;
    }

    /** Object Filters **/
    public supportsObjectFilters(): boolean {
        return !!this.objectFiltersStore;
    }

    public getCommittedObjectFiltersSnapshot(): ApplicationFilters | null {
        return this.getSavedObjectFilterState(
            this.getLastAppliedObjectFiltersStateKey(),
        );
    }

    @computed
    public get lastAppliedSourceFiltersForObjectFiltering():
        | ApplicationFilters
        | null {
        return this.lastAppliedSourceFilters;
    }

    public getLastAppliedObjectFiltersSnapshot(): ApplicationFilters | null {
        return _.cloneDeep(this.lastAppliedObjectFiltersPayload?.objectFilters ?? null);
    }

    public getLastAppliedObjectFiltersPayload():
        | SignalsPersistedObjectFilters
        | null {
        return _.cloneDeep(this.lastAppliedObjectFiltersPayload);
    }

    public getCommittedObjectRequestFiltersSnapshot():
        | ApplicationFilters
        | null {
        return this.getSavedObjectFilterState(
            this.getLastAppliedObjectRequestFiltersStateKey(),
        );
    }

    public isCommittedDateOverrideEnabled(): boolean {
        return this._committedDateOverride;
    }

    public isCommittedDateTypeOverrideEnabled(): boolean {
        return this._committedDateTypeOverride;
    }

    public isCommittedCallDurationOverrideEnabled(): boolean {
        return this._committedCallDurationOverride;
    }

    public isCommittedMessageCountOverrideEnabled(): boolean {
        return this._committedMessageCountOverride;
    }

    public objectFiltersChangedSinceLastApply(): boolean {
        return !_.isEqual(
            this.buildObjectFilterPayloadFromState(
                this.objectFiltersStore?.toFilterObject() ?? null,
                this.objectFilterDateOverride,
                this.objectFilterDateTypeOverride,
                this.objectFilterCallDurationOverride,
                this.objectFilterMessageCountOverride,
            ),
            this.lastAppliedObjectFiltersPayload,
        );
    }

    public compareCommittedObjectFiltersAgainstSource(
        sourceFilters: ApplicationFilters,
    ): FiltersComparisonResult | null {
        const committedObjectFilters = this.getCommittedObjectFiltersSnapshot();
        if (!committedObjectFilters || !this.objectFiltersStore) {
            return null;
        }

        const comparisonKeys = getObjectFilterKeysFromFilters(
            committedObjectFilters,
        );
        return compareFiltersByKey(
            sourceFilters,
            committedObjectFilters,
            comparisonKeys,
            { agents: this.applicationFilterValuesStore.agents },
        );
    }

    public getCommittedObjectFilterConflictsAgainstSource(
        sourceFilters: ApplicationFilters,
    ) {
        const committedObjectFilters = this.getCommittedObjectFiltersSnapshot();
        if (!committedObjectFilters || !this.objectFiltersStore) {
            return [];
        }

        return getPersistedObjectFilterConflictsAgainstSource(
            sourceFilters,
            {
                objectFilters: committedObjectFilters,
                objectFilterDateOverride: this._committedDateOverride,
                objectFilterDateTypeOverride: this._committedDateTypeOverride,
                objectFilterCallDurationOverride:
                    this._committedCallDurationOverride,
                objectFilterMessageCountOverride:
                    this._committedMessageCountOverride,
            },
            { agents: this.applicationFilterValuesStore.agents },
        );
    }

    @action
    public removeConflictingCommittedObjectFilterValues(
        sourceFilters: ApplicationFilters,
    ): boolean {
        const committedObjectFilters = this.getCommittedObjectFiltersSnapshot();
        if (!committedObjectFilters || !this.objectFiltersStore) {
            return false;
        }

        const currentPersistedFilters: SignalsPersistedObjectFilters = {
            objectFilters: committedObjectFilters,
            objectFilterDateOverride: this._committedDateOverride,
            objectFilterDateTypeOverride: this._committedDateTypeOverride,
            objectFilterCallDurationOverride: this._committedCallDurationOverride,
            objectFilterMessageCountOverride: this._committedMessageCountOverride,
        };
        const nextPersistedFilters = resolvePersistedObjectFiltersAgainstSource(
            sourceFilters,
            currentPersistedFilters,
            { agents: this.applicationFilterValuesStore.agents },
        );
        if (_.isEqual(currentPersistedFilters, nextPersistedFilters)) {
            return false;
        }

        this.objectFiltersStore.applyExternalFilters(
            _.cloneDeep(nextPersistedFilters.objectFilters),
        );
        this._committedDateOverride =
            nextPersistedFilters.objectFilterDateOverride;
        this._committedDateTypeOverride =
            nextPersistedFilters.objectFilterDateTypeOverride;
        this._committedCallDurationOverride =
            nextPersistedFilters.objectFilterCallDurationOverride ?? false;
        this._committedMessageCountOverride =
            nextPersistedFilters.objectFilterMessageCountOverride ?? false;
        this.objectFilterDateOverride = this._committedDateOverride;
        this.objectFilterDateTypeOverride = this._committedDateTypeOverride;
        this.objectFilterCallDurationOverride =
            this._committedCallDurationOverride;
        this.objectFilterMessageCountOverride =
            this._committedMessageCountOverride;
        this.saveCurrentObjectFilterStatesFromDraft();

        return true;
    }

    public async persistCommittedObjectFilters(): Promise<boolean> {
        if (!this.objectFiltersStore) {
            return true;
        }

        return this.persistObjectFiltersPayload({
            objectFilters: _.cloneDeep(this.objectFiltersStore.toFilterObject()),
            objectFilterDateOverride: this.objectFilterDateOverride,
            objectFilterDateTypeOverride: this.objectFilterDateTypeOverride,
            objectFilterCallDurationOverride: this.objectFilterCallDurationOverride,
            objectFilterMessageCountOverride: this.objectFilterMessageCountOverride,
        });
    }

    @action
    public commitObjectFilters() {
        if (!this.objectFiltersStore) return;

        this._committedDateOverride = this.objectFilterDateOverride;
        this._committedDateTypeOverride = this.objectFilterDateTypeOverride;
        this._committedCallDurationOverride =
            this.objectFilterCallDurationOverride;
        this._committedMessageCountOverride =
            this.objectFilterMessageCountOverride;
        this.saveCurrentObjectFilterStatesFromDraft();
    }

    @action
    public discardObjectFilterDraft() {
        if (!this.objectFiltersStore) return;

        const lastAppliedState = this.getCommittedObjectFiltersSnapshot();

        if (lastAppliedState) {
            this.objectFiltersStore.applyExternalFilters(
                _.cloneDeep(lastAppliedState),
            );
        } else {
            this.objectFiltersStore.resetFilters();
        }
        this.objectFilterDateOverride = this._committedDateOverride;
        this.objectFilterDateTypeOverride = this._committedDateTypeOverride;
        this.objectFilterCallDurationOverride =
            this._committedCallDurationOverride;
        this.objectFilterMessageCountOverride =
            this._committedMessageCountOverride;
    }

    public objectFiltersChangedSinceCommit(): boolean {
        if (!this.objectFiltersStore) return false;

        const committed: Partial<ApplicationFilters> = _.cloneDeep(
            this.getCommittedObjectFiltersSnapshot() ?? {},
        );
        const draft: Partial<ApplicationFilters> = _.cloneDeep(
            this.objectFiltersStore.toFilterObject(),
        );

        if (!this.objectFilterDateOverride && !this._committedDateOverride) {
            draft.startDate = committed.startDate;
            draft.endDate = committed.endDate;
            draft.dateRangeLabel = committed.dateRangeLabel;
        }

        if (
            !this.objectFilterDateTypeOverride &&
            !this._committedDateTypeOverride
        ) {
            draft.dateReferenceOption = committed.dateReferenceOption;
            (draft as any).dateReference = (committed as any).dateReference;
        }

        const filtersChanged = !_.isEqual(draft, committed);
        const dateOverrideChanged =
            this.objectFilterDateOverride !== this._committedDateOverride;
        const dateTypeOverrideChanged =
            this.objectFilterDateTypeOverride !==
            this._committedDateTypeOverride;
        const callDurationOverrideChanged =
            this.objectFilterCallDurationOverride !==
            this._committedCallDurationOverride;
        const messageCountOverrideChanged =
            this.objectFilterMessageCountOverride !==
            this._committedMessageCountOverride;

        return (
            filtersChanged ||
            dateOverrideChanged ||
            dateTypeOverrideChanged ||
            callDurationOverrideChanged ||
            messageCountOverrideChanged
        );
    }

    public getSerializedObjectFiltersForPersistence(): string | undefined {
        if (!this.objectFiltersStore) return undefined;

        return JSON.stringify({
            objectFilters: _.cloneDeep(this.objectFiltersStore.toFilterObject()),
            objectFilterDateOverride: this.objectFilterDateOverride,
            objectFilterDateTypeOverride: this.objectFilterDateTypeOverride,
            objectFilterCallDurationOverride: this.objectFilterCallDurationOverride,
            objectFilterMessageCountOverride: this.objectFilterMessageCountOverride,
        } satisfies SignalsPersistedObjectFilters);
    }

    @computed
    get hasCommittedObjectFilters(): boolean {
        if (
            this._committedDateOverride ||
            this._committedDateTypeOverride ||
            this._committedCallDurationOverride ||
            this._committedMessageCountOverride
        ) {
            return true;
        }
        const committedObjectFilters = this.getCommittedObjectFiltersSnapshot();
        if (!committedObjectFilters) return false;

        return getObjectFilterKeysFromFilters(committedObjectFilters).some(
            (key) => {
                if (isExtendedMetadataObjectFilterKey(key)) {
                    const metadataKey = key.replace("extendedMetadata.", "");
                    const groupedValue =
                        committedObjectFilters.extendedMetadataGrouped?.[
                            metadataKey
                        ];
                    if (!isFilterValueEmpty(groupedValue?.values)) {
                        return true;
                    }

                    const value =
                        committedObjectFilters.extendedMetadata?.[metadataKey];
                    return !isFilterValueEmpty(value);
                }

                if (key === "callDuration") {
                    return (
                        (committedObjectFilters.minCallDuration ?? 0) > 0 ||
                        (committedObjectFilters.maxCallDuration ?? 0) > 0
                    );
                }

                if (key === "messageCount") {
                    return (
                        (committedObjectFilters.minMessageCount ?? 0) > 0 ||
                        (committedObjectFilters.maxMessageCount ?? 0) > 0
                    );
                }

                const value = (committedObjectFilters as any)[key];
                return !isFilterValueEmpty(value);
            },
        );
    }

    /**
     * Whether a deprecated filter is applied to this object.
     *
     * Reads the merged request filters, so a deprecated filter counts whether it came from the
     * dashboard or from this object's own object filters -- the two are indistinguishable to
     * anything consuming the data, and the merge has already resolved which one wins.
     *
     * Only simple-valued filters are deprecated today. If a compound key (`callDuration`,
     * `messageCount`, `extendedMetadata.*`) is ever deprecated, it needs the per-key value handling
     * `hasCommittedObjectFilters` does rather than this bare lookup.
     */
    @computed
    private get hasAppliedDeprecatedFilter(): boolean {
        const mergedFilters = this.getMergedFiltersForRequest() as unknown as Record<
            string,
            unknown
        >;

        return this.applicationFilterItemsStore.deprecatedFilterKeys.some(
            (key) => !isFilterValueEmpty(mergedFilters[key]),
        );
    }

    /**
     * Whether this object groups by a deprecated dimension.
     *
     * A group-by option is deprecated exactly when the filter it groups on is -- grouping by
     * Primary Themes (Topic-backed) reads the deprecated `topics` filter, and Primary Topic reads
     * `primaryTopics`. Derived through `GROUP_BY_OPTION_CONFIG`, the same link the Group By menu
     * uses to inherit an option's status, so the two cannot disagree about which are deprecated.
     *
     * Reads `state.groupBy` -- the canonical grouping on the request that produced the data on
     * screen -- rather than the chart's draft configuration, so this describes what a KBo answer
     * would actually be about.
     */
    @computed
    private get isGroupedByDeprecatedDimension(): boolean {
        const grouping = (this.state as SignalsRequest | undefined)?.groupBy;
        if (grouping?.kind !== "categorical") return false;

        return this.applicationFilterItemsStore.isDeprecatedFilterKey(
            GROUP_BY_OPTION_CONFIG[grouping.option]?.filterKey,
        );
    }

    /**
     * Whether KBo has deprecated data in scope for this object, and so cannot say anything useful
     * about it -- the Topic data stopped populating on 7/1/26. The point menu drops its Ask KBo
     * action entirely when this holds.
     *
     * Entitlement-blind on purpose: unlike the Deprecated tag, which only renders for viewers who
     * have the replacement, this applies to every tenant, because the data gap does too. See
     * ApplicationFilterItemsStore.isDeprecatedFilterKey.
     */
    @computed
    get hasDeprecatedDataForKbo(): boolean {
        return (
            this.isGroupedByDeprecatedDimension ||
            this.hasAppliedDeprecatedFilter
        );
    }

    public getMergedFiltersForRequest():
        | ReturnType<ApplicationFiltersStore["toRequestObject"]>
        | ApplicationFilters {
        const sourceFilters = this.getSourceFiltersForObjectFiltering();
        const committedObjectRequestFilters =
            this.getCommittedObjectRequestFiltersSnapshot();
        if (!committedObjectRequestFilters || !this.objectFiltersStore) {
            return this.applicationFilterItemsStore.stripInaccessibleFilters(
                BaseObjectStore.synchronizeDateReferenceFields(sourceFilters),
                { requestObject: true },
            ).filters;
        }

        // Re-resolve committed object filters against the current source baseline on
        // every request so stale persisted values can never widen a newer dashboard filter.
        const resolvedCommittedFilters = resolvePersistedObjectFiltersAgainstSource(
            sourceFilters,
            {
                objectFilters: committedObjectRequestFilters,
                objectFilterDateOverride: this._committedDateOverride,
                objectFilterDateTypeOverride: this._committedDateTypeOverride,
                objectFilterCallDurationOverride:
                    this._committedCallDurationOverride,
                objectFilterMessageCountOverride:
                    this._committedMessageCountOverride,
            },
            { agents: this.applicationFilterValuesStore.agents },
        );
        const clampedObjectFilters = resolvedCommittedFilters.objectFilters;
        const comparisonKeys = getObjectFilterKeysFromFilters(
            clampedObjectFilters,
        );
        const mergedFilters = _.cloneDeep(sourceFilters);

        comparisonKeys.forEach((key) => {
            if (isExtendedMetadataObjectFilterKey(key)) return;

            if (key === "callDuration") {
                const hasCallDurationOverride =
                    (clampedObjectFilters.minCallDuration ?? 0) > 0 ||
                    (clampedObjectFilters.maxCallDuration ?? 0) > 0;

                if (!hasCallDurationOverride) return;

                mergedFilters.minCallDuration =
                    clampedObjectFilters.minCallDuration;
                mergedFilters.maxCallDuration =
                    clampedObjectFilters.maxCallDuration;
                return;
            }

            if (key === "messageCount") {
                const hasMessageCountOverride =
                    (clampedObjectFilters.minMessageCount ?? 0) > 0 ||
                    (clampedObjectFilters.maxMessageCount ?? 0) > 0;

                if (!hasMessageCountOverride) return;

                mergedFilters.minMessageCount =
                    clampedObjectFilters.minMessageCount;
                mergedFilters.maxMessageCount =
                    clampedObjectFilters.maxMessageCount;
                return;
            }

            if (key === "wordsAndPhrasesSearchString") {
                return;
            }

            // Module score conditions are ANDed with the dashboard's rather than replacing them.
            // Every condition is its own EXISTS clause on the backend and the clauses are ANDed,
            // so keeping both sets narrows the population the dashboard selected and can never
            // widen it, whatever modules the chart names. The chart's own block repeats the
            // dashboard's conditions when it was seeded from them, so identical ones are dropped
            // rather than sent twice.
            if (key === "moduleScores") {
                const chartModuleScores = clampedObjectFilters.moduleScores ?? [];
                if (chartModuleScores.length === 0) return;

                const seen = new Set<string>();
                mergedFilters.moduleScores = [
                    ...(sourceFilters.moduleScores ?? []),
                    ...chartModuleScores,
                ].filter((condition) => {
                    const identity = `${condition.licensedModuleId}::${condition.operator}::${condition.moduleScore}`;
                    if (seen.has(identity)) return false;
                    seen.add(identity);
                    return true;
                });
                return;
            }

            const value = (clampedObjectFilters as any)[key];
            if (!isFilterValueEmpty(value)) {
                (mergedFilters as any)[key] = value;

                const modeCompanionKey = getModeCompanionKeyForFilterKey(key);
                if (!modeCompanionKey) return;

                const clampedMode = (clampedObjectFilters as any)[
                    modeCompanionKey
                ];
                const requestMode = (committedObjectRequestFilters as any)[
                    modeCompanionKey
                ];

                if (clampedMode !== undefined) {
                    (mergedFilters as any)[modeCompanionKey] = clampedMode;
                } else if (requestMode !== undefined) {
                    (mergedFilters as any)[modeCompanionKey] = requestMode;
                }
            }
        });

        const mergedWordsAndPhrasesSearchString =
            combineWordsAndPhrasesSearchStrings(
                sourceFilters.wordsAndPhrasesSearchString,
                clampedObjectFilters.wordsAndPhrasesSearchString,
            );
        const hasObjectWordsAndPhrasesValue = !!normalizeWordsAndPhrasesSearchString(
            clampedObjectFilters.wordsAndPhrasesSearchString,
        );
        const hasSourceWordsAndPhrasesValue = !!normalizeWordsAndPhrasesSearchString(
            sourceFilters.wordsAndPhrasesSearchString,
        );

        mergedFilters.wordsAndPhrasesSearchString =
            mergedWordsAndPhrasesSearchString;

        if (hasObjectWordsAndPhrasesValue && hasSourceWordsAndPhrasesValue) {
            mergedFilters.wordsAndPhrasesItems = _.cloneDeep(
                initWordsPhrasesItems,
            );
            mergedFilters.wordsAndPhrasesIsInputStandard = false;
        } else if (hasObjectWordsAndPhrasesValue) {
            mergedFilters.wordsAndPhrasesItems = _.cloneDeep(
                clampedObjectFilters.wordsAndPhrasesItems ??
                    initWordsPhrasesItems,
            );
            mergedFilters.wordsAndPhrasesIsInputStandard =
                clampedObjectFilters.wordsAndPhrasesIsInputStandard ?? true;
        }

        if (!isFilterValueEmpty(clampedObjectFilters.hierarchyIds)) {
            mergedFilters.selectedCategories =
                clampedObjectFilters.selectedCategories ?? [];
            mergedFilters.selectedSubcategories =
                clampedObjectFilters.selectedSubcategories ?? [];
        }

        if (
            clampedObjectFilters.extendedMetadata &&
            Object.keys(clampedObjectFilters.extendedMetadata).length > 0
        ) {
            // Metadata merges by individual metadata key so an object can narrow one
            // field without discarding unrelated metadata filters from the source.
            mergedFilters.extendedMetadata = {
                ...(mergedFilters.extendedMetadata ?? {}),
                ...clampedObjectFilters.extendedMetadata,
            };
        }

        if (
            clampedObjectFilters.extendedMetadataGrouped &&
            Object.keys(clampedObjectFilters.extendedMetadataGrouped).length > 0
        ) {
            mergedFilters.extendedMetadataGrouped = {
                ...(mergedFilters.extendedMetadataGrouped ?? {}),
                ...clampedObjectFilters.extendedMetadataGrouped,
            };
        }

        if (this._committedDateOverride) {
            mergedFilters.startDate = committedObjectRequestFilters.startDate;
            mergedFilters.endDate = committedObjectRequestFilters.endDate;
            mergedFilters.dateRangeLabel =
                committedObjectRequestFilters.dateRangeLabel ?? undefined;
        }

        if (resolvedCommittedFilters.objectFilterDateTypeOverride) {
            const committedDateReferenceOption =
                clampedObjectFilters.dateReferenceOption;
            mergedFilters.dateReferenceOption = committedDateReferenceOption;
            (mergedFilters as any).dateReference =
                (clampedObjectFilters as any).dateReference ??
                committedDateReferenceOption;
        }

        return this.applicationFilterItemsStore.stripInaccessibleFilters(
            BaseObjectStore.synchronizeDateReferenceFields(mergedFilters),
            { requestObject: true },
        ).filters;
    }

    private static synchronizeDateReferenceFields<T extends ApplicationFilters>(
        filters: T,
    ): T {
        const effectiveDateReference =
            filters.dateReferenceOption ?? (filters as any).dateReference;

        if (!effectiveDateReference) {
            return filters;
        }

        filters.dateReferenceOption = effectiveDateReference;
        (filters as T & { dateReference?: typeof effectiveDateReference })
            .dateReference = effectiveDateReference;

        return filters;
    }

    private getSourceFiltersForObjectFiltering(): ApplicationFilters {
        const dashboardAppliedFilters = this.getDashboardAppliedFilters?.();
        if (!dashboardAppliedFilters) {
            return this.applicationFiltersStore.toRequestObject();
        }

        if (!this.dashboardAppliedFiltersStore) {
            this.dashboardAppliedFiltersStore =
                new ApplicationFiltersInstanceStore(
                    this.rootStore,
                    `dashboard-applied-filters-${this.id}`,
                );
        }

        this.dashboardAppliedFiltersStore.applyExternalFilters(
            _.cloneDeep(dashboardAppliedFilters),
            false,
        );

        // applyExternalFilters updates words/phrases string through a nested reaction.
        // Set the concrete value directly so the request built immediately below
        // always uses the latest dashboard-applied search phrase in this same tick.
        this.dashboardAppliedFiltersStore.setWordsAndPhrasesSearchString(
            dashboardAppliedFilters.wordsAndPhrasesSearchString,
        );

        return this.dashboardAppliedFiltersStore.toRequestObject();
    }

    private hydratePersistedObjectFilters(rawPayload?: string | null) {
        if (!this.objectFiltersStore) return;

        const parsedPayload = this.parsePersistedObjectFilters(rawPayload);
        const synchronizedPayload =
            parsedPayload &&
            this.applicationFilterItemsStore.extendedMetadataFields.length > 0
                ? synchronizePersistedObjectFiltersExtendedMetadata(
                      parsedPayload,
                      this.applicationFilterItemsStore.extendedMetadataFields,
                  )
                : undefined;
        const hydratedPayload =
            synchronizedPayload?.persistedObjectFilters ?? parsedPayload;

        if (hydratedPayload) {
            this.objectFiltersStore.applyExternalFilters(
                _.cloneDeep(hydratedPayload.objectFilters),
            );
        }

        const dateOverride = hydratedPayload?.objectFilterDateOverride ?? false;
        const dateTypeOverride =
            hydratedPayload?.objectFilterDateTypeOverride ?? false;
        const hasNonDefaultCallDuration =
            (hydratedPayload?.objectFilters.minCallDuration ?? 0) > 0 ||
            (hydratedPayload?.objectFilters.maxCallDuration ?? 0) > 0;
        const callDurationOverride =
            hydratedPayload?.objectFilterCallDurationOverride ??
            hasNonDefaultCallDuration;
        const hasNonDefaultMessageCount =
            (hydratedPayload?.objectFilters.minMessageCount ?? 0) > 0 ||
            (hydratedPayload?.objectFilters.maxMessageCount ?? 0) > 0;
        const messageCountOverride =
            hydratedPayload?.objectFilterMessageCountOverride ??
            hasNonDefaultMessageCount;
        this.objectFilterDateOverride = dateOverride;
        this._committedDateOverride = dateOverride;
        this.objectFilterDateTypeOverride = dateTypeOverride;
        this._committedDateTypeOverride = dateTypeOverride;
        this.objectFilterCallDurationOverride = callDurationOverride;
        this._committedCallDurationOverride = callDurationOverride;
        this.objectFilterMessageCountOverride = messageCountOverride;
        this._committedMessageCountOverride = messageCountOverride;
        this.saveCurrentObjectFilterStatesFromDraft();

        if (synchronizedPayload?.changed) {
            void this.persistObjectFiltersPayload(
                synchronizedPayload.persistedObjectFilters,
            );
        }
    }

    private parsePersistedObjectFilters(
        rawPayload?: string | null,
    ): SignalsPersistedObjectFilters | null {
        if (!rawPayload) return null;

        try {
            const parsed = JSON.parse(rawPayload) as unknown;
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
                return null;
            }

            const payloadRecord = parsed as {
                objectFilters?: unknown;
                objectFilterDateOverride?: unknown;
                objectFilterDateTypeOverride?: unknown;
                objectFilterCallDurationOverride?: unknown;
                objectFilterMessageCountOverride?: unknown;
            };

            const filters =
                payloadRecord.objectFilters &&
                typeof payloadRecord.objectFilters === "object" &&
                !Array.isArray(payloadRecord.objectFilters)
                    ? (_.cloneDeep(
                          payloadRecord.objectFilters,
                      ) as ApplicationFilters)
                    : ({} as ApplicationFilters);

            return {
                objectFilters: filters,
                objectFilterDateOverride:
                    typeof payloadRecord.objectFilterDateOverride === "boolean"
                        ? payloadRecord.objectFilterDateOverride
                        : false,
                objectFilterDateTypeOverride:
                    typeof payloadRecord.objectFilterDateTypeOverride ===
                    "boolean"
                        ? payloadRecord.objectFilterDateTypeOverride
                        : false,
                objectFilterCallDurationOverride:
                    typeof payloadRecord.objectFilterCallDurationOverride ===
                    "boolean"
                        ? payloadRecord.objectFilterCallDurationOverride
                        : undefined,
                objectFilterMessageCountOverride:
                    typeof payloadRecord.objectFilterMessageCountOverride ===
                    "boolean"
                        ? payloadRecord.objectFilterMessageCountOverride
                        : undefined,
            };
        } catch (_error) {
            return null;
        }
    }

    private getLastAppliedObjectFiltersStateKey(): string {
        return `lastAppliedFilters - ${this.id}`;
    }

    private getLastAppliedObjectRequestFiltersStateKey(): string {
        return `lastAppliedRequestFilters - ${this.id}`;
    }

    private getSavedObjectFilterState(stateKey: string): ApplicationFilters | null {
        if (!this.objectFiltersStore) return null;

        const savedState = this.objectFiltersStore.savedFilterStates[stateKey];
        if (!savedState) return null;

        return _.cloneDeep(savedState);
    }

    private buildObjectFilterPayloadFromState(
        filters: ApplicationFilters | null,
        objectFilterDateOverride: boolean,
        objectFilterDateTypeOverride: boolean,
        objectFilterCallDurationOverride: boolean,
        objectFilterMessageCountOverride: boolean,
    ): SignalsPersistedObjectFilters | null {
        if (!filters) return null;

        const hasActiveObjectFilters =
            objectFilterDateOverride ||
            objectFilterDateTypeOverride ||
            objectFilterCallDurationOverride ||
            objectFilterMessageCountOverride ||
            getObjectFilterKeysFromFilters(filters).some((key) => {
                if (isExtendedMetadataObjectFilterKey(key)) {
                    const metadataKey = key.replace("extendedMetadata.", "");
                    const groupedValue =
                        filters.extendedMetadataGrouped?.[metadataKey];
                    if (!isFilterValueEmpty(groupedValue?.values)) {
                        return true;
                    }

                    const value = filters.extendedMetadata?.[metadataKey];
                    return !isFilterValueEmpty(value);
                }

                if (key === "callDuration") {
                    return (
                        (filters.minCallDuration ?? 0) > 0 ||
                        (filters.maxCallDuration ?? 0) > 0
                    );
                }

                if (key === "messageCount") {
                    return (
                        (filters.minMessageCount ?? 0) > 0 ||
                        (filters.maxMessageCount ?? 0) > 0
                    );
                }

                const value = (filters as any)[key];
                return !isFilterValueEmpty(value);
            });

        if (!hasActiveObjectFilters) return null;

        return {
            objectFilters: _.cloneDeep(filters),
            objectFilterDateOverride,
            objectFilterDateTypeOverride,
            objectFilterCallDurationOverride,
            objectFilterMessageCountOverride,
        };
    }

    private buildRequestObjectForFilters(
        filters: ApplicationFilters,
    ): ApplicationFilters {
        const requestStore = new ApplicationFiltersInstanceStore(
            this.rootStore,
            `object-filter-request-builder-${this.id}`,
        );
        requestStore.applyExternalFilters(_.cloneDeep(filters), false);

        // applyExternalFilters updates words/phrases through a nested reaction.
        // Set the concrete value directly so the request snapshot built here
        // cannot serialize the default empty standard-item state first.
        requestStore.setWordsAndPhrasesSearchString(
            filters.wordsAndPhrasesSearchString,
        );

        return requestStore.toRequestObject();
    }

    private saveObjectFilterStates(filters: ApplicationFilters) {
        if (!this.objectFiltersStore) return;

        this.objectFiltersStore.saveExternalFilterState(
            this.getLastAppliedObjectFiltersStateKey(),
            _.cloneDeep(filters),
        );
        this.objectFiltersStore.saveExternalFilterState(
            this.getLastAppliedObjectRequestFiltersStateKey(),
            this.buildRequestObjectForFilters(filters),
        );
    }

    private saveCurrentObjectFilterStatesFromDraft() {
        if (!this.objectFiltersStore) return;

        this.saveObjectFilterStates(this.objectFiltersStore.toFilterObject());
    }

    @action
    private setLastAppliedObjectFiltersFromCommittedState() {
        this.lastAppliedObjectFiltersPayload =
            this.buildObjectFilterPayloadFromState(
                this.getCommittedObjectFiltersSnapshot(),
                this._committedDateOverride,
                this._committedDateTypeOverride,
                this._committedCallDurationOverride,
                this._committedMessageCountOverride,
            );
    }

    private async synchronizeObjectFilterExtendedMetadata() {
        if (!this.objectFiltersStore) return;

        const extendedMetadataFields =
            this.applicationFilterItemsStore.extendedMetadataFields;
        if (extendedMetadataFields.length === 0) return;

        const committedObjectFilters = this.getCommittedObjectFiltersSnapshot();
        if (!committedObjectFilters) return;

        const committedPersistedFilters: SignalsPersistedObjectFilters = {
            objectFilters: committedObjectFilters,
            objectFilterDateOverride: this._committedDateOverride,
            objectFilterDateTypeOverride: this._committedDateTypeOverride,
            objectFilterCallDurationOverride: this._committedCallDurationOverride,
            objectFilterMessageCountOverride: this._committedMessageCountOverride,
        };
        const synchronizedCommittedFilters =
            synchronizePersistedObjectFiltersExtendedMetadata(
                committedPersistedFilters,
                extendedMetadataFields,
            );

        const draftPersistedFilters: SignalsPersistedObjectFilters = {
            objectFilters: this.objectFiltersStore.toFilterObject(),
            objectFilterDateOverride: this.objectFilterDateOverride,
            objectFilterDateTypeOverride: this.objectFilterDateTypeOverride,
            objectFilterCallDurationOverride: this.objectFilterCallDurationOverride,
            objectFilterMessageCountOverride: this.objectFilterMessageCountOverride,
        };
        const synchronizedDraftFilters =
            synchronizePersistedObjectFiltersExtendedMetadata(
                draftPersistedFilters,
                extendedMetadataFields,
            );

        if (
            !synchronizedCommittedFilters.changed &&
            !synchronizedDraftFilters.changed
        ) {
            return;
        }

        this.objectFiltersStore.applyExternalFilters(
            _.cloneDeep(synchronizedDraftFilters.persistedObjectFilters.objectFilters),
        );
        this.saveObjectFilterStates(
            synchronizedCommittedFilters.persistedObjectFilters.objectFilters,
        );

        await this.persistObjectFiltersPayload(
            synchronizedCommittedFilters.persistedObjectFilters,
        );
    }

    private async persistObjectFiltersPayload(
        payload: SignalsPersistedObjectFilters,
    ): Promise<boolean> {
        // Standard dashboards are editable for session-only changes. Conflict
        // resolution and migration paths must honor the same persistence guard
        // as the normal object-configuration save path.
        if (!this.isEditable || !this.isSaveable) {
            return true;
        }

        const serializedObjectFilters = JSON.stringify(payload);

        try {
            switch (this.objectType) {
                case SignalsObjectType.Chart:
                    await this.signalsService.updateSignalsChart({
                        id: this.id,
                        objectFilters: serializedObjectFilters,
                    });
                    return true;
                case SignalsObjectType.Table:
                    await this.signalsService.updateSignalsTableCard({
                        id: this.id,
                        objectFilters: serializedObjectFilters,
                    });
                    return true;
                case SignalsObjectType.Unique:
                    await this.signalsService.updateSignalsUniqueCard({
                        id: this.id,
                        objectFilters: serializedObjectFilters,
                    });
                    return true;
                case SignalsObjectType.SmallCard:
                    await this.signalsService.updateSignalsSmallCard({
                        id: this.id,
                        objectFilters: serializedObjectFilters,
                    });
                    return true;
                default:
                    return true;
            }
        } catch {
            return false;
        }
    }

    /** Viz Options **/
    protected initializeVizOptions(rawVizOptions?: string | null): void {
        if (rawVizOptions === undefined) {
            return;
        }

        this.vizOptions = rawVizOptions;
        const {
            parsedVizOptions,
            shouldPersistVizOptionsCleanup,
        } = this.parseInitialVizOptions(rawVizOptions);
        this.setParsedVizOptions(parsedVizOptions);

        if (shouldPersistVizOptionsCleanup) {
            void this.persistVizOptionsCleanup(parsedVizOptions);
        }
    }

    protected setParsedVizOptions(
        parsedVizOptions: SignalsParsedVizOptions | null,
    ): void {
        this.parsedVizOptions = parsedVizOptions;
        this.vizOptions = parsedVizOptions
            ? JSON.stringify(parsedVizOptions)
            : null;
    }

    protected getSelectedIdsForVizDefaults(): string[] {
        return [];
    }

    protected cleanupLegacyShowOnlyLockedVizOptions(
        parsedVizOptions: SignalsParsedVizOptions & {
            showOnlyLocked?: boolean;
        },
    ): {
        cleanedVizOptions: SignalsParsedVizOptions;
        shouldPersistVizOptionsCleanup: boolean;
    } {
        const hasShowOnlyLockedFlag = Object.prototype.hasOwnProperty.call(
            parsedVizOptions,
            "showOnlyLocked",
        );
        const { showOnlyLocked, ...parsedWithoutShowOnlyLocked } =
            parsedVizOptions;

        return {
            cleanedVizOptions: {
                ...parsedWithoutShowOnlyLocked,
                defaultSelectedIds:
                    showOnlyLocked !== false
                        ? Array.isArray(
                              parsedWithoutShowOnlyLocked.defaultSelectedIds,
                          )
                            ? parsedWithoutShowOnlyLocked.defaultSelectedIds
                                  .map((id) => `${id}`.trim())
                                  .filter((id) => id.length > 0)
                            : []
                        : [],
            },
            shouldPersistVizOptionsCleanup: hasShowOnlyLockedFlag,
        };
    }

    protected parseInitialVizOptions(
        rawVizOptions?: string | null,
    ): {
        parsedVizOptions: SignalsParsedVizOptions | null;
        shouldPersistVizOptionsCleanup: boolean;
    } {
        if (!rawVizOptions) {
            return {
                parsedVizOptions: null,
                shouldPersistVizOptionsCleanup: false,
            };
        }

        try {
            const parsed = JSON.parse(rawVizOptions) as unknown;
            if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
                return {
                    parsedVizOptions: null,
                    shouldPersistVizOptionsCleanup: false,
                };
            }
            const {
                cleanedVizOptions,
                shouldPersistVizOptionsCleanup,
            } = this.cleanupLegacyShowOnlyLockedVizOptions(
                parsed as SignalsParsedVizOptions & {
                    showOnlyLocked?: boolean;
                },
            );

            return {
                parsedVizOptions: cleanedVizOptions,
                shouldPersistVizOptionsCleanup,
            };
        } catch (_error) {
            return {
                parsedVizOptions: null,
                shouldPersistVizOptionsCleanup: false,
            };
        }
    }

    private async persistVizOptionsCleanup(
        parsedVizOptions: SignalsParsedVizOptions | null,
    ): Promise<void> {
        const serializedVizOptions = JSON.stringify(parsedVizOptions ?? {});

        try {
            switch (this.objectType) {
                case SignalsObjectType.Chart:
                    await this.signalsService.updateSignalsChart({
                        id: this.id,
                        vizOptions: serializedVizOptions,
                    });
                    break;
                case SignalsObjectType.Table:
                    await this.signalsService.updateSignalsTableCard({
                        id: this.id,
                        vizOptions: serializedVizOptions,
                    });
                    break;
                default:
                    break;
            }
        } catch {
            // Best-effort migration cleanup; keep rendering even if persistence fails.
        }
    }

    public getEffectiveVizOptionsForPersistence(): string | undefined {
        if (
            this.objectType !== SignalsObjectType.Chart &&
            this.objectType !== SignalsObjectType.Table
        ) {
            return undefined;
        }

        return JSON.stringify({
            ...(this.parsedVizOptions ?? {}),
            defaultSelectedIds: this.getSelectedIdsForVizDefaults(),
        } satisfies SignalsParsedVizOptions);
    }
}

export default BaseObjectStore;
